<template>
	<v-row class="full-height po-generate-page">
		<v-col cols="12" class="flex-none pb-0">
			<v-card class="po-generate-toolbar-card" flat>
				<v-card-text class="pa-3">
					<v-row align="center" dense>
						<!-- Search -->
						<v-col cols="12" sm="6" md="4" lg="3">
							<v-text-field
								v-model="searchForm.keywords"
								placeholder="Style, Description, Order #, Customer..."
								prepend-inner-icon="mdi-magnify"
								outlined
								dense
								hide-details
								clearable
								class="po-generate-search"
								@input="onSearchInput"
								@click:clear="onSearchClear"
							></v-text-field>
						</v-col>

						<!-- Spacer -->
						<v-spacer class="d-none d-md-flex"></v-spacer>

						<!-- Create Stock PO Button -->
						<v-col cols="auto">
							<v-btn
								depressed
								rounded
								color="success"
								@click="generateManualPo()"
								:disabled="selectedRows.length == 0 || !canPlaceStockPO()"
							>
								Create Stock PO
							</v-btn>
						</v-col>
					</v-row>
				</v-card-text>
			</v-card>
		</v-col>

		<v-col cols="12" class="flex-auto pt-0 po-generate-table-container">
			<v-data-table
				v-model="selectedItems"
				:headers="headers"
				:items="posToGenerateLineItems"
				:loading="loading"
				:options.sync="tableOptions"
				:server-items-length="totalCount"
				:footer-props="footerProps"
				:mobile-breakpoint="0"
				class="po-generate-data-table"
				show-select
				item-key="guid"
				must-sort
				fixed-header
				@item-selected="onItemSelected"
				@toggle-select-all="onToggleSelectAll"
			>
				<!-- Custom checkbox header slot -->
				<template v-slot:header.data-table-select>
					<v-checkbox
						v-if="selectedRows.length > 0"
						:input-value="allMatchingVendorSelected"
						:indeterminate="someMatchingVendorSelected && !allMatchingVendorSelected"
						class="form-checkbox-center"
						color="accent"
						hide-details
						@change="selectAllMatchingVendorRows()"
					></v-checkbox>
				</template>

				<!-- Custom checkbox cell slot -->
				<template v-slot:item.data-table-select="{ item, isSelected }">
					<v-checkbox
						:input-value="isSelected"
						:disabled="!canPlaceStockPO() || (selectedRows.length > 0 && item.vendorId !== filterByVendorId)"
						class="form-checkbox-center"
						color="accent"
						hide-details
						@change="onCheckboxChange(item, $event)"
					></v-checkbox>
				</template>

				<!-- Style Column -->
				<template v-slot:item.styleCode="{ item }">
					<div class="table-icon">
						<SystemCategoryIcon :system-category-id="item.systemCategoryId" />
						{{ item.styleCode }}
					</div>
				</template>

				<!-- Order Number Column -->
				<template v-slot:item.orderNumber="{ item }">
					<router-link :to="{ path: `/sales-orders/orders/${item.salesOrderId}` }">
						{{ item.orderNumber }}
					</router-link>
				</template>

				<!-- Order Date Column -->
				<template v-slot:item.orderDate="{ item }">
					{{ formatDate(item.orderDate) }}
				</template>

				<!-- Vendor Column -->
				<template v-slot:item.vendorId="{ item }">
					{{ renderVendor(item.vendorId) }}
				</template>

				<!-- Loading slot -->
				<template v-slot:loading>
					<v-skeleton-loader type="table-row@10"></v-skeleton-loader>
				</template>

				<!-- No data slot -->
				<template v-slot:no-data>
					<div class="text-center pa-4">
						<v-icon large color="grey lighten-1">mdi-package-variant</v-icon>
						<p class="mt-2 grey--text">No POs to generate found</p>
					</div>
				</template>
			</v-data-table>
		</v-col>
	</v-row>
</template>

<script>
import gql from "graphql-tag";
import SystemCategoryIcon from "@/components/SystemCategoryIcon.vue";
import { VENDOR_BY_ID } from "@/gql/vendors";
import * as signalR from "@microsoft/signalr";
import { hasPermission, SystemPermissions } from "@/models/User";

export default {
	name: "POToGenerate",
	components: { SystemCategoryIcon },
	data() {
		return {
			loading: true,
			signalRConnection: null,
			searchForm: {
				keywords: "",
			},
			vendors: [],
			activePO: null,
			selectedItems: [],
			selectedRows: [],
			filterByVendorId: null,
			totalCount: 0,
			posToGenerateLineItems: [],
			searchTimeout: null,
			tableOptions: {
				page: 1,
				itemsPerPage: 15,
				sortBy: ["lineItemId"],
				sortDesc: [true],
			},
			footerProps: {
				"items-per-page-options": [10, 15, 25, 50],
				"show-first-last-page": true,
			},
			headers: [
				{
					text: "Style",
					value: "styleCode",
					sortable: true,
					width: "14%",
				},
				{
					text: "Order Number",
					value: "orderNumber",
					sortable: true,
					width: "12%",
					align: "center",
				},
				{
					text: "Line Number",
					value: "lineNumber",
					sortable: true,
					width: "11%",
					align: "center",
				},
				{
					text: "Order Date",
					value: "orderDate",
					sortable: true,
					width: "11%",
					align: "center",
				},
				{
					text: "Size",
					value: "sizeName",
					sortable: true,
					width: "6%",
					align: "center",
				},
				{
					text: "Need",
					value: "need",
					sortable: true,
					width: "8%",
					align: "center",
				},
				{
					text: "On Order",
					value: "onOrder",
					sortable: true,
					width: "8%",
					align: "center",
				},
				{
					text: "Vendor",
					value: "vendorId",
					sortable: true,
					width: "12%",
				},
				{
					text: "Customer",
					value: "customer",
					sortable: true,
					width: "14%",
				},
			],
		};
	},
	apollo: {
		posToGenerateQuery: {
			fetchPolicy: "network-only",
			query() {
				return gql`
					query PurchaseOrdersToGenerateLineItems(
						$offset: Int!
						$take: Int!
						$order: [StagedBackOrderedStockItemSortInput!] = [{ lineItemId: DESC }]
						$where: StagedBackOrderedStockItemFilterInput = null
					) {
						purchaseOrdersToGenerateLineItems(
							skip: $offset
							take: $take
							where: $where
							order: $order
						) {
							totalCount
							items {
								guid
								systemCategoryId
								styleCode
								styleHash
								description
								lineItemId
								lineNumber
								expectedDate
								orderNumber
								orderDate
								salesOrderId
								companyName
								customer
								vendorId
								sizeId
								sizeName
								sizeNeedSelections {
									lengthId
									quantity
								}
								need
								onOrder
							}
							pageInfo {
								hasNextPage
								hasPreviousPage
							}
						}
					}
				`;
			},
			variables() {
				return this.buildQueryVariables();
			},
			update(data) {
				this.loading = false;
				this.totalCount = data.purchaseOrdersToGenerateLineItems?.totalCount || 0;
				this.posToGenerateLineItems = data.purchaseOrdersToGenerateLineItems?.items || [];
				return data.purchaseOrdersToGenerateLineItems;
			},
			error(error) {
				console.error("Error fetching POs to generate:", error);
				this.loading = false;
			},
		},
	},
	computed: {
		uniqueVendorIds() {
			const vendorIds = this.posToGenerateLineItems.map((order) => order.vendorId);
			return [...new Set(vendorIds)];
		},
		manualPoItems() {
			return this.mergeItems(this.selectedItems);
		},
		navigationDirection() {
			return this.$store.getters["globalState/navigationDirection"];
		},
		allMatchingVendorSelected() {
			if (!this.filterByVendorId || this.selectedRows.length === 0) return false;
			const matchingItems = this.posToGenerateLineItems.filter(
				(item) => item.vendorId === this.filterByVendorId
			);
			return matchingItems.every((item) =>
				this.selectedItems.some((selected) => selected.guid === item.guid)
			);
		},
		someMatchingVendorSelected() {
			if (!this.filterByVendorId || this.selectedRows.length === 0) return false;
			const matchingItems = this.posToGenerateLineItems.filter(
				(item) => item.vendorId === this.filterByVendorId
			);
			return matchingItems.some((item) =>
				this.selectedItems.some((selected) => selected.guid === item.guid)
			);
		},
	},
	watch: {
		tableOptions: {
			handler() {
				this.fetchData();
			},
			deep: true,
		},
		"$route.query.search": {
			handler(newSearch) {
				if (newSearch) {
					this.searchForm.keywords = newSearch;
					this.tableOptions.page = 1;
				}
			},
			immediate: true,
		},
		navigationDirection: {
			handler(newDirection) {
				if (newDirection && !this.$route.query.search) {
					const savedState = this.$store.getters["globalState/poGenerateState"];
					if (savedState) {
						// Restore page from offset
						const page = Math.floor(savedState.offset / this.tableOptions.itemsPerPage) + 1;
						this.tableOptions.page = page;
						this.searchForm.keywords = savedState.keywords;
						this.selectedRows = savedState.selectedRows || [];
						this.filterByVendorId = savedState.filterByVendorId;
					}
				}
			},
			immediate: true,
		},
		posToGenerateLineItems(value) {
			this.vendors = [];
			this.posToGenerateLineItems.forEach(async (po) => {
				let vendor = await this.getVendorById(po.vendorId);
				if (vendor) {
					this.vendors.push(vendor);
				}
			});

			// Restore selections based on selectedRows indices
			this.$nextTick(() => {
				if (this.selectedRows.length > 0 && this.filterByVendorId) {
					this.selectedItems = this.posToGenerateLineItems.filter(
						(item) =>
							item.vendorId === this.filterByVendorId &&
							this.selectedRows.some((rowIdx) => {
								const originalItem = this.posToGenerateLineItems[rowIdx];
								return originalItem && originalItem.guid === item.guid;
							})
					);
				}

				// Auto-select items if coming from a jumplink with autoSelect=true
				this.autoSelectMatchingItems();
			});
		},
		selectedItems(value) {
			// Update selectedRows to track indices
			this.selectedRows = value.map((item) =>
				this.posToGenerateLineItems.findIndex((po) => po.guid === item.guid)
			);
		},
	},
	methods: {
		canPlaceStockPO() {
			const user = this.$store.getters.userInfo;
			if (hasPermission(user, SystemPermissions.Purchasing_POsToGenerate_PlaceStockPO)) {
				return true;
			}
			return user.isAdmin;
		},

		formatDate(date) {
			if (!date) return "";
			date = date.substring(0, 10);
			const [year, month, day] = date.split("-");
			return `${month}/${day}/${year}`;
		},

		renderVendor(vendorId) {
			let vendorById = this.vendors.find((v) => v.vendorId == vendorId);
			return vendorById ? vendorById.companyName : null;
		},

		// Map table column values to GraphQL sort field names
		getSortFieldName(columnValue) {
			const sortFieldMap = {
				styleCode: "styleCode",
				sizeName: "sizeName",
				orderNumber: "orderNumber",
				lineNumber: "lineNumber",
				orderDate: "orderDate",
				vendorId: "vendorId",
				customer: "customer",
				need: "need",
				onOrder: "onOrder",
				lineItemId: "lineItemId",
			};
			return sortFieldMap[columnValue] || "lineItemId";
		},

		buildQueryVariables() {
			const { page, itemsPerPage, sortBy, sortDesc } = this.tableOptions;
			const searchTerm = (this.searchForm.keywords || "").trim();

			let where = {
				need: { gt: 0 },
			};

			if (searchTerm) {
				where = {
					and: [
						{ need: { gt: 0 } },
						{
							or: [
								{ styleCode: { contains: searchTerm } },
								{ description: { contains: searchTerm } },
								{ orderNumber: { contains: searchTerm } },
								{ customer: { contains: searchTerm } },
								{ companyName: { contains: searchTerm } },
								{ sizeName: { contains: searchTerm } },
							],
						},
					],
				};
			}

			// Build dynamic sort order
			const sortColumn = sortBy[0] || "lineItemId";
			const sortDirection = sortDesc[0] ? "DESC" : "ASC";
			const sortFieldName = this.getSortFieldName(sortColumn);
			const order = [{ [sortFieldName]: sortDirection }];

			return {
				where,
				offset: (page - 1) * itemsPerPage,
				take: itemsPerPage,
				order,
			};
		},

		async fetchData() {
			this.loading = true;
			if (this.$apollo.queries.posToGenerateQuery) {
				await this.$apollo.queries.posToGenerateQuery.refetch();
			}
		},

		onSearchInput() {
			// Debounced search
			if (this.searchTimeout) {
				clearTimeout(this.searchTimeout);
			}
			this.searchTimeout = setTimeout(() => {
				this.tableOptions.page = 1;
				this.fetchData();
			}, 400);
		},

		onSearchClear() {
			if (this.searchTimeout) {
				clearTimeout(this.searchTimeout);
			}
			this.tableOptions.page = 1;
			this.fetchData();
		},

		onCheckboxChange(item, isSelected) {
			if (isSelected) {
				// Set vendor filter on first selection
				if (this.selectedRows.length === 0) {
					this.filterByVendorId = item.vendorId;
				}
				// Only add if matches vendor filter
				if (item.vendorId === this.filterByVendorId) {
					this.selectedItems = [...this.selectedItems, item];
				}
			} else {
				this.selectedItems = this.selectedItems.filter((i) => i.guid !== item.guid);
				// Reset vendor filter if no selections
				if (this.selectedItems.length === 0) {
					this.filterByVendorId = null;
				}
			}
		},

		onItemSelected({ item, value }) {
			// This is called by v-data-table but we handle selection via onCheckboxChange
		},

		onToggleSelectAll({ items, value }) {
			// Handle select all - disabled in our case since we have vendor filtering
		},

		selectAllMatchingVendorRows() {
			if (!this.filterByVendorId) return;

			const matchingItems = this.posToGenerateLineItems.filter(
				(item) => item.vendorId === this.filterByVendorId
			);

			// Check if all matching items are already selected
			const allSelected = matchingItems.every((item) =>
				this.selectedItems.some((selected) => selected.guid === item.guid)
			);

			if (allSelected) {
				// Deselect all
				this.selectedItems = [];
				this.filterByVendorId = null;
			} else {
				// Select all matching vendor items
				this.selectedItems = [...matchingItems];
			}
		},

		autoSelectMatchingItems() {
			const { styleCode, orderNumber, autoSelect } = this.$route.query;

			// Only auto-select if autoSelect flag is set and we have the required params
			if (autoSelect !== "true" || !styleCode || !orderNumber) {
				return;
			}

			// Find all items matching both styleCode and orderNumber
			const matchingItems = this.posToGenerateLineItems.filter(
				(item) => item.styleCode === styleCode && item.orderNumber === orderNumber
			);

			if (matchingItems.length === 0) {
				return;
			}

			// Check if all matching items have the same vendor
			const firstVendorId = matchingItems[0].vendorId;
			const allSameVendor = matchingItems.every((m) => m.vendorId === firstVendorId);

			// Clear any previous selection
			this.selectedItems = [];
			this.filterByVendorId = null;

			// Select matching items (if different vendors, only select items with first vendor)
			const itemsToSelect = matchingItems.filter(
				(item) => allSameVendor || item.vendorId === firstVendorId
			);

			this.selectedItems = itemsToSelect;
			if (this.selectedItems.length > 0) {
				this.filterByVendorId = firstVendorId;
			}

			// Clear the autoSelect query param to prevent re-triggering on data refresh
			if (this.$route.query.autoSelect) {
				this.$router.replace({
					path: this.$route.path,
					query: {
						...this.$route.query,
						autoSelect: undefined,
					},
				});
			}
		},

		generateManualPo() {
			this.$store.dispatch("updateManualPoItems", this.manualPoItems);

			//route to purchase order entry screen and generate a po with the assigned vendor and po items
			this.$router.push({
				path: "/purchasing/purchase-orders/new",
				query: { generateManualPo: true },
			});
		},

		async getVendorById(id) {
			const resVendor = await this.$apollo.query({
				fetchPolicy: "no-cache",
				query: VENDOR_BY_ID,
				variables: {
					id: id,
				},
			});
			const vendorSearch = resVendor.data.response;
			return vendorSearch ? vendorSearch : null;
		},

		mergeItems(items) {
			let result = [];

			// Use a map for easy lookup of existing items by keys
			let itemsByKey = new Map();

			for (let item of items) {
				let {
					guid,
					sizeId,
					sizeName,
					sizeLengthId,
					need,
					onOrder,
					sizeNeedSelections,
					...keyProps
				} = item;
				// Combine styleCode and salesOrderId as the key
				let key = `${item.styleCode}:${item.styleHash}` + "-" + item.salesOrderId;

				if (!itemsByKey.has(key)) {
					// Clone key properties and add new sizes and ids arrays for new items
					let newItem = {
						...keyProps,
						sizes: [],
						ids: [guid],
					};

					// If sizeNeedSelections is present and is an array with elements, use it to populate sizes
					if (Array.isArray(sizeNeedSelections) && sizeNeedSelections.length > 0) {
						sizeNeedSelections.forEach((selection) => {
							newItem.sizes.push({
								sizeId,
								sizeName,
								sizeLengthId: selection.lengthId,
								need: selection.quantity,
								onOrder,
							});
						});
					} else {
						// If sizeNeedSelections is not present or is empty, use the original sizeLengthId and need
						newItem.sizes.push({ sizeId, sizeName, sizeLengthId, need, onOrder });
					}

					result.push(newItem);
					itemsByKey.set(key, newItem);
				} else {
					// If sizeNeedSelections is present and has elements, add each selection to sizes
					if (Array.isArray(sizeNeedSelections) && sizeNeedSelections.length > 0) {
						sizeNeedSelections.forEach((selection) => {
							itemsByKey.get(key).sizes.push({
								sizeId,
								sizeName,
								sizeLengthId: selection.lengthId,
								need: selection.quantity,
								onOrder,
							});
						});
					} else {
						// If sizeNeedSelections is not present or is empty, add the original size information to sizes
						itemsByKey.get(key).sizes.push({ sizeId, sizeName, sizeLengthId, need, onOrder });
					}

					// Add the id to the ids array of the matching item
					itemsByKey.get(key).ids.push(guid);
				}
			}

			return result;
		},
	},
	mounted() {
		this.signalRConnection = new signalR.HubConnectionBuilder()
			.withUrl("/updateHub")
			.configureLogging(signalR.LogLevel.Information)
			.withAutomaticReconnect([0, 5000, 5000, 5000, 5000])
			.build();

		this.signalRConnection.start().catch((err) => console.error(err.toString()));
		this.signalRConnection.on("BackOrderedStockUpdated", () => {
			this.fetchData();
		});
	},

	beforeRouteLeave(to, from, next) {
		const offset = (this.tableOptions.page - 1) * this.tableOptions.itemsPerPage;
		// Save state before leaving
		this.$store.dispatch("globalState/savePoGenerateState", {
			offset,
			keywords: this.searchForm.keywords,
			selectedRows: this.selectedRows,
			filterByVendorId: this.filterByVendorId,
			scrollPosition: window.scrollY,
		});
		next();
	},

	beforeDestroy() {
		if (this.searchTimeout) {
			clearTimeout(this.searchTimeout);
		}
		if (this.signalRConnection) {
			this.signalRConnection.stop();
		}
	},
};
</script>

<style lang="scss" scoped>
.po-generate-page {
	// Use calc to fit within viewport: 100vh - header (64px) - container padding (24px)
	height: calc(100vh - 88px);
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

.po-generate-table-container {
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0; // Critical for flex children to respect overflow
	overflow: hidden;
}

.po-generate-toolbar-card {
	border-radius: 4px 4px 0 0 !important;
	border-bottom: 1px solid $color-gray;
	flex-shrink: 0; // Don't shrink the toolbar
}

.po-generate-search {
	::v-deep .v-input__slot {
		background-color: $color-gray-light-2 !important;
	}
}

.po-generate-data-table {
	border-radius: 0 0 4px 4px !important;
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0; // Critical for flex children to respect overflow
	overflow: hidden;

	// Table wrapper should scroll
	::v-deep .v-data-table__wrapper {
		flex: 1;
		overflow-y: auto;
		overflow-x: auto;

		table {
			min-width: 1100px;
		}
	}

	// Keep footer fixed at bottom
	::v-deep .v-data-footer {
		flex-shrink: 0;
	}

	// Match existing header styling
	::v-deep > .v-data-table__wrapper > table > thead > tr > th {
		font-size: 1.4rem !important;
		font-weight: 500 !important;
		color: #000 !important;
		text-transform: uppercase;
		background-color: $color-gray-light-2 !important;
		white-space: nowrap;
	}

	// Match existing row height and font size
	::v-deep > .v-data-table__wrapper > table > tbody > tr > td {
		height: 60px !important;
		font-size: 1.6rem !important;
	}

	// Footer styling
	::v-deep .v-data-footer {
		font-size: 1.25rem !important;
	}

	::v-deep .v-data-footer__select .v-select__selections .v-select__selection--comma {
		font-size: 1.15rem !important;
	}

	// Style for table icon (system category + code)
	.table-icon {
		display: flex;
		align-items: center;
		gap: 0.5rem;
	}
}
</style>
